import React from 'react';

const Chapter8Content = () => {
  return (
    <article className="prose prose-lg max-w-none">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Chapter Eight</h1>
        <p className="text-xl text-gray-600">Seventeen Simple Witnesses</p>
        <p className="text-lg text-gray-500 mt-2">What the Bible Really Says — Without Any Greek Required</p>
        <div className="flex justify-center gap-4 mt-4">
          <span className="text-2xl text-amber-600">✦</span>
          <span className="text-2xl text-amber-600">✦</span>
          <span className="text-2xl text-amber-600">✦</span>
        </div>
      </div>

      <div className="mb-12">
        <p className="text-gray-800 leading-relaxed mb-4">
          Here are seventeen witnesses from the Bible. Each one is a passage or a plain piece of reasoning that the pretribulation system must work around. Each one, when you read it plainly, says something different from what you and I may have been taught.
        </p>
        <p className="text-gray-800 leading-relaxed mb-4">
          You do not need Greek. You do not need a commentary. You just need to read what it says.
        </p>
      </div>

      {/* WITNESS ONE */}
      <div className="mb-12">
        <div className="bg-blue-50 border-l-4 border-blue-600 p-6 mb-4">
          <h2 className="text-xl font-bold text-blue-900">WITNESS ONE</h2>
          <h3 className="text-lg font-semibold text-blue-800">The Load-Bearing Wall Was Already Demolished</h3>
          <p className="text-sm text-blue-700 mt-1">Ephesians 2:14</p>
        </div>
        <p className="text-gray-800 leading-relaxed mb-4">
          The entire pretribulation system rests on one foundation: Israel and the Church are permanently separate programs, and the tribulation belongs to Israel's program — not the Church's. But Paul, writing about what the cross accomplished, says: "He has broken down the middle wall of partition between us." The Greek verbs are past tense — completed actions. The wall is already down. What the cross demolished, no theological system can rebuild. There is one olive tree. One covenant family. One gathering. Before, and seemingly at the moment the national physical corporate commonwealth of Israel, whom the church is grafted into spiritual by faith, says, "blessed is he who comes in the Name of the Lord", when they "look upon Him whom they crucified" and the Last trumpets sounds on that day of Atonement - the rapture of the Church and the remnant of physical Israel and the grafted in church happens. One event. One people. One nation. One church. One Lord. The Millennial Kingdom comes on Earth as it is in Heaven.
        </p>
      </div>

      {/* WITNESS TWO */}
      <div className="mb-12">
        <div className="bg-green-50 border-l-4 border-green-600 p-6 mb-4">
          <h2 className="text-xl font-bold text-green-900">WITNESS TWO</h2>
          <h3 className="text-lg font-semibold text-green-800">Wrath and Tribulation Are Not the Same Thing</h3>
          <p className="text-sm text-green-700 mt-1">1 Thessalonians 5:9</p>
        </div>
        <p className="text-gray-800 leading-relaxed mb-4">
          It is often taught that the great tribulation and the wrath of God are the same event. This idea has been repeated so widely that most believers have simply assumed the Bible equates the two. But the text does not say this — and the distinction matters enormously, because once you separate them, one of the main pillars of the pretribulation argument loses its footing.
        </p>
        <p className="text-gray-800 leading-relaxed mb-4">
          God's wrath is His judgment poured out on the unbelieving world. The tribulation is the fury of Satan and the Antichrist against the saints, the church and the physical nation of Israel. These are two different things from two different sources aimed at two different targets. The promise is that we are not appointed to wrath — not that we will never face tribulation. The three Hebrew men were in the furnace. But the fire had no power over them. They came out without the smell of smoke.
        </p>
      </div>

      {/* WITNESS THREE through SEVENTEEN - continuing with same pattern */}
      <div className="space-y-12">
        <div className="bg-purple-50 border-l-4 border-purple-600 p-6">
          <h2 className="text-xl font-bold text-purple-900">WITNESS THREE</h2>
          <h3 className="text-lg font-semibold text-purple-800">The Restrainer Is Michael the Archangel</h3>
          <p className="text-sm text-purple-700 mt-1">Daniel 12:1, Revelation 12:7–9</p>
        </div>

        <div className="bg-amber-50 border-l-4 border-amber-600 p-6">
          <h2 className="text-xl font-bold text-amber-900">WITNESS FOUR</h2>
          <h3 className="text-lg font-semibold text-amber-800">The Elect of Matthew 24 Is the Church</h3>
          <p className="text-sm text-amber-700 mt-1">Mark 13:37</p>
        </div>

        <div className="bg-red-50 border-l-4 border-red-600 p-6">
          <h2 className="text-xl font-bold text-red-900">WITNESS FIVE</h2>
          <h3 className="text-lg font-semibold text-red-800">"Kept From the Hour" Means Preserved Through It</h3>
          <p className="text-sm text-red-700 mt-1">John 17:15</p>
        </div>

        <div className="bg-indigo-50 border-l-4 border-indigo-600 p-6">
          <h2 className="text-xl font-bold text-indigo-900">WITNESS SIX</h2>
          <h3 className="text-lg font-semibold text-indigo-800">"Come Up Hither" Is Not the Rapture</h3>
          <p className="text-sm text-indigo-700 mt-1">Revelation 4:1</p>
        </div>

        <div className="bg-pink-50 border-l-4 border-pink-600 p-6">
          <h2 className="text-xl font-bold text-pink-900">WITNESS SEVEN</h2>
          <h3 className="text-lg font-semibold text-pink-800">The "One Taken" Is Taken to Judgment</h3>
          <p className="text-sm text-pink-700 mt-1">Matthew 24:37–39</p>
        </div>

        <div className="bg-teal-50 border-l-4 border-teal-600 p-6">
          <h2 className="text-xl font-bold text-teal-900">WITNESS EIGHT</h2>
          <h3 className="text-lg font-semibold text-teal-800">Noah, Lot, and Enoch Don't Prove Pre-Trib Removal</h3>
          <p className="text-sm text-teal-700 mt-1">Genesis 6–8, 19</p>
        </div>

        <div className="bg-orange-50 border-l-4 border-orange-600 p-6">
          <h2 className="text-xl font-bold text-orange-900">WITNESS NINE</h2>
          <h3 className="text-lg font-semibold text-orange-800">The "Any-Moment" Rapture Contradicts Paul</h3>
          <p className="text-sm text-orange-700 mt-1">2 Thessalonians 2:3</p>
        </div>

        <div className="bg-lime-50 border-l-4 border-lime-600 p-6">
          <h2 className="text-xl font-bold text-lime-900">WITNESS TEN</h2>
          <h3 className="text-lg font-semibold text-lime-800">The Tribulation Is Not "Israel's Program Only"</h3>
          <p className="text-sm text-lime-700 mt-1">Galatians 3:29, Romans 11:17</p>
        </div>

        <div className="bg-cyan-50 border-l-4 border-cyan-600 p-6">
          <h2 className="text-xl font-bold text-cyan-900">WITNESS ELEVEN</h2>
          <h3 className="text-lg font-semibold text-cyan-800">The Door Shuts When He Arrives</h3>
          <p className="text-sm text-cyan-700 mt-1">Matthew 25:10</p>
        </div>

        <div className="bg-fuchsia-50 border-l-4 border-fuchsia-600 p-6">
          <h2 className="text-xl font-bold text-fuchsia-900">WITNESS TWELVE</h2>
          <h3 className="text-lg font-semibold text-fuchsia-800">The Twenty-Four Elders Are Not the Raptured Church</h3>
          <p className="text-sm text-fuchsia-700 mt-1">Revelation 4:4</p>
        </div>

        <div className="bg-rose-50 border-l-4 border-rose-600 p-6">
          <h2 className="text-xl font-bold text-rose-900">WITNESS THIRTEEN</h2>
          <h3 className="text-lg font-semibold text-rose-800">There Are Not Two Separate Comings</h3>
          <p className="text-sm text-rose-700 mt-1">2 Thessalonians 2:1, 8</p>
        </div>

        <div className="bg-sky-50 border-l-4 border-sky-600 p-6">
          <h2 className="text-xl font-bold text-sky-900">WITNESS FOURTEEN</h2>
          <h3 className="text-lg font-semibold text-sky-800">Paul Uses One Word for One Arrival</h3>
          <p className="text-sm text-sky-700 mt-1">2 Thessalonians 2:1, 8</p>
        </div>

        <div className="bg-violet-50 border-l-4 border-violet-600 p-6">
          <h2 className="text-xl font-bold text-violet-900">WITNESS FIFTEEN</h2>
          <h3 className="text-lg font-semibold text-violet-800">"I Go to Prepare a Place" Is Not a Pre-Trib Promise</h3>
          <p className="text-sm text-violet-700 mt-1">John 14:2–3, John 6:39–54</p>
        </div>

        <div className="bg-emerald-50 border-l-4 border-emerald-600 p-6">
          <h2 className="text-xl font-bold text-emerald-900">WITNESS SIXTEEN</h2>
          <h3 className="text-lg font-semibold text-emerald-800">The Thessalonians' Alarm Proves Their Expectation</h3>
          <p className="text-sm text-emerald-700 mt-1">2 Thessalonians 2:2</p>
        </div>

        <div className="bg-amber-50 border-l-4 border-amber-600 p-6">
          <h2 className="text-xl font-bold text-amber-900">WITNESS SEVENTEEN</h2>
          <h3 className="text-lg font-semibold text-amber-800">The Blessed Hope Is a Visible Appearing</h3>
          <p className="text-sm text-amber-700 mt-1">Titus 2:13</p>
        </div>
      </div>

      <div className="flex justify-center gap-4 my-8">
        <span className="text-2xl text-amber-600">✦</span>
        <span className="text-2xl text-amber-600">✦</span>
        <span className="text-2xl text-amber-600">✦</span>
      </div>

      {/* Closing */}
      <div className="mt-12 mb-8">
        <p className="text-gray-800 leading-relaxed mb-4">
          Some of us may be young. But Grandmother knows who she is. Looking forward to that great Hope. She does not feel like a helpless evacuee. She feels the shofar in her hand, the sound of praise and shout in her Breath. She knows the sound of the Tekiah Gedolah is about to sound— and she knows she's part of the Kingdom escort Guard.
        </p>
        <div className="bg-blue-50 border-l-4 border-blue-600 p-6 my-6">
          <p className="text-blue-900 italic">
            Blessed [is] the people that know the joyful sound: they shall walk, O LORD, in the light of thy countenance. — Psalm 89:15
          </p>
        </div>
      </div>
    </article>
  );
};

export default Chapter8Content;
